/**
 * The toolbar's one single-choice segmented selector: a time window, a view
 * mode, an ordering, a Recorte, a kind filter. No caller restyles it.
 *
 * Wraps the kit's `toggle-group`, with the drawn `SegmentedItem` look. An
 * item with an `icon` shows no text and carries its name in a tooltip and
 * `aria-label`; its `hint`, when given, replaces the name in the tooltip.
 */

import type { ReactNode } from 'react';

import { IconHint } from '../../../chrome/hint';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  hint?: string;
}

export function SegmentedControl<T extends string>(props: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
}): ReactNode {
  return (
    <ToggleGroup
      type="single"
      spacing={1}
      aria-label={props.label}
      value={props.value}
      // Radix reports an empty string when the selected item is pressed again;
      // a segmented selector always has one option chosen.
      onValueChange={(next) => {
        if (next !== '') props.onChange(next as T);
      }}
      className="bg-secondary rounded-pill p-1"
    >
      {props.options.map((option) => {
        const item = (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            aria-label={option.icon !== undefined ? option.label : undefined}
            // One fixed height, so a text control and an icon control line up.
            className={`${SEGMENTED_ITEM_CLASSNAME} h-7 ${option.icon !== undefined ? 'px-2' : ''}`}
          >
            {option.icon ?? option.label}
          </ToggleGroupItem>
        );
        if (option.icon === undefined) return item;
        return (
          <IconHint key={option.value} label={option.hint ?? option.label}>
            {item}
          </IconHint>
        );
      })}
    </ToggleGroup>
  );
}
